import React, { Fragment, useEffect, useState } from "react";
import { Alert, Grid } from "@mui/material";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { ArrowBack, PersonAdd } from "@mui/icons-material";
import { initializeApp, deleteApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import app from "../../firebase";
import { useUser } from "../context/UserContext";

const CreateAdmin = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { userRole, loading: userLoading } = useUser();

  useEffect(() => {
    if (!userLoading && (userRole === "editor" || userRole === "operator")) navigate("/home");
  }, [userLoading, userRole, navigate]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email.toLowerCase());
  };

  const handleCreate = async () => {
    if (!name) return setErrorMsg("Name cannot be empty");
    if (!validateEmail(email)) return setErrorMsg("Please enter a valid email");
    if (!password) return setErrorMsg("Password cannot be empty");
    if (!role) return setErrorMsg("Please select a role");

    setLoading(true);
    try {
      const secondaryApp = initializeApp(app.options, "secondary_" + Date.now());
      const secondaryAuth = getAuth(secondaryApp);

      const userCred = await createUserWithEmailAndPassword(secondaryAuth, email, password);
      const uid = userCred.user.uid;

      await signOut(secondaryAuth);
      await deleteApp(secondaryApp);

      await setDoc(doc(db, "users", uid), {
        name,
        email,
        role,
        createdAt: serverTimestamp(),
      });

      setName("");
      setEmail("");
      setPassword("");
      setRole("");

      toast("Admin created successfully!", { type: "success" });
      setTimeout(() => navigate("/manage-admin"), 2000);
    } catch (error) {
      if (error.code === "auth/email-already-in-use") {
        setErrorMsg("An account with this email already exists");
      } else {
        setErrorMsg("Error creating admin: " + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-4 py-3 border border-gray-200 rounded-xl font-abel text-sm text-gray-700 focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition bg-white";
  const labelClass = "block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 font-abel";

  return (
    <Fragment>
      <div className="min-h-screen bg-[#f5f7fe] p-3 sm:p-6">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate("/manage-admin")}
              className="p-2 rounded-xl hover:bg-white border border-transparent hover:border-gray-100 transition"
            >
              <ArrowBack className="text-gray-400" style={{ fontSize: 20 }} />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#000C66] font-aref">Create Admin</h1>
              <p className="text-gray-400 text-xs font-abel mt-0.5">Add a new administrator account</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-8">
            {errorMsg && <Alert severity="error" className="mb-5" onClose={() => setErrorMsg("")}>{errorMsg}</Alert>}

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <label className={labelClass}>Full Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setErrorMsg(""); }}
                  placeholder="Admin's full name"
                  className={inputClass}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <label className={labelClass}>Email *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setErrorMsg(""); }}
                  placeholder="admin@example.com"
                  className={inputClass}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <label className={labelClass}>Password *</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setErrorMsg(""); }}
                  placeholder="••••••••"
                  className={inputClass}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <label className={labelClass}>Role *</label>
                <select
                  value={role}
                  onChange={(e) => { setRole(e.target.value); setErrorMsg(""); }}
                  className={inputClass}
                >
                  <option value="">-- Select Role --</option>
                  <option value="operator">Operator</option>
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                </select>
              </Grid>

              <Grid item xs={12}>
                <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                  <p className="text-xs text-amber-700 font-abel">
                    <strong>Note:</strong> Super Admins can manage other admins and view all activity logs. Regular admins have standard content management access.
                  </p>
                </div>
              </Grid>

              <Grid item xs={12} className="flex justify-end">
                <button
                  onClick={handleCreate}
                  disabled={loading}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-[#000C66] text-white px-8 py-3 rounded-xl font-abel font-semibold hover:bg-blue-900 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <PersonAdd style={{ fontSize: 18 }} />
                  )}
                  {loading ? "Creating..." : "Create Admin"}
                </button>
              </Grid>
            </Grid>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default CreateAdmin;
